/**
 * Collectible Entity
 *
 * A Phaser Container representing a collectible letter or number in the overworld.
 * Renders as colorful floating text (large bold letters/numbers) with a gentle
 * vertical oscillation to attract the child's attention. Uses Phaser text rendering
 * instead of sprites — no external image assets needed.
 *
 * On collection, plays a scale-up + fade-out + particle burst.
 * Follows the same float animation pattern as the PowerUp entity.
 *
 * Requirements: 7.2, 7.4, 7.5
 */

import Phaser from 'phaser';
import type { CollectibleType } from '../systems/CollectibleSystem';
import { LAYER_DEPTHS } from '../scenes/GameScene';

// ─── Float Animation Configuration ───────────────────────────────────────────
/** Pixels of vertical oscillation (sine wave amplitude) */
const FLOAT_AMPLITUDE = 8;
/** Cycles per second for the floating animation */
const FLOAT_SPEED = 1.5;

// ─── Collision Body Size ──────────────────────────────────────────────────────
const BODY_WIDTH = 44;
const BODY_HEIGHT = 44;

// ─── Collection Animation Configuration ──────────────────────────────────────
const COLLECT_SCALE = 1.8;
const COLLECT_DURATION = 350;
const PARTICLE_COUNT = 8;
const PARTICLE_SPREAD = 40;
const PARTICLE_LIFETIME = 400;

// ─── Letter/Number Color Palettes ─────────────────────────────────────────────
const LETTER_COLORS = [
  '#FF6B6B', '#FFA500', '#FFD700', '#48C774', '#3273DC', '#B86BFF',
  '#FF85C0', '#00D1FF', '#FF4757', '#2ED573', '#1E90FF', '#FF6348',
  '#FFC312', '#7BED9F', '#70A1FF', '#FF9FF3', '#48DBFB', '#FECA57',
  '#F368E0', '#0ABDE3', '#10AC84', '#EE5A24', '#5F27CD', '#01A3A4',
  '#C44569', '#F8B500',
];
const NUMBER_COLORS = [
  '#00D1FF', '#48C774', '#FFD700', '#FF6B6B', '#3273DC',
  '#FF85C0', '#FFA500', '#B86BFF', '#2ED573',
];

export interface CollectibleConfig {
  id: string;               // "letter-A" or "number-3"
  type: CollectibleType;
  value: string;
  spriteKey: string;        // kept for interface compatibility (not used for rendering)
  x: number;
  y: number;
}

export class Collectible extends Phaser.GameObjects.Container {
  /** The main text display (letter or number) */
  private textDisplay: Phaser.GameObjects.Text;

  /** Optional glow circle behind the text */
  private glowCircle: Phaser.GameObjects.Arc;

  /** The collectible ID (e.g. "letter-A", "number-3") */
  private _id: string;

  /** The collectible type ('letter' or 'number') */
  private _type: CollectibleType;

  /** The collectible value (e.g. "A", "3") */
  private _value: string;

  /** Whether this collectible has been collected */
  private _isCollected: boolean = false;

  /** Base Y position (before float offset) */
  private baseY: number;

  /** Internal time accumulator for float animation */
  private elapsed: number = 0;

  constructor(scene: Phaser.Scene, config: CollectibleConfig) {
    super(scene, config.x, config.y);

    this._id = config.id;
    this._type = config.type;
    this._value = config.value;
    this.baseY = config.y;

    // Pick color based on type and value
    const color = this.getColor();

    // Create a subtle glow circle behind the text
    this.glowCircle = scene.add.circle(0, 0, 22, Phaser.Display.Color.HexStringToColor(color).color, 0.2);
    this.add(this.glowCircle);

    // Create the main text (large, bold, colorful)
    this.textDisplay = scene.add.text(0, 0, config.value, {
      fontSize: '32px',
      fontFamily: 'Arial Black, Arial, sans-serif',
      color: color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
      align: 'center',
    });
    this.textDisplay.setOrigin(0.5, 0.5);
    this.add(this.textDisplay);

    // Set container size for physics overlap detection
    this.setSize(BODY_WIDTH, BODY_HEIGHT);

    // Set depth to POWER_UPS layer (18)
    this.setDepth(LAYER_DEPTHS.POWER_UPS);

    // Add to scene
    scene.add.existing(this as unknown as Phaser.GameObjects.GameObject);
  }

  // ─── Accessors ──────────────────────────────────────────────────────────────

  /** Gets the collectible ID. */
  get id(): string {
    return this._id;
  }

  /** Gets the collectible type. */
  get collectibleType(): CollectibleType {
    return this._type;
  }

  /** Gets the collectible value. */
  get value(): string {
    return this._value;
  }

  /** Gets whether this collectible has been collected. */
  get isCollected(): boolean {
    return this._isCollected;
  }

  // ─── Update ─────────────────────────────────────────────────────────────────

  /**
   * Updates the floating animation.
   * Should be called every frame from the scene's update loop.
   *
   * Requirement 7.2: Gentle floating animation (vertical oscillation).
   *
   * @param dt - Delta time in milliseconds
   */
  update(dt: number): void {
    if (this._isCollected) return;

    this.elapsed += dt / 1000;

    // Sinusoidal floating (vertical oscillation) — same pattern as PowerUp
    const floatOffset = Math.sin(this.elapsed * FLOAT_SPEED * Math.PI * 2) * FLOAT_AMPLITUDE;
    this.y = this.baseY + floatOffset;
  }

  // ─── Collection ─────────────────────────────────────────────────────────────

  /**
   * Collects the item: plays collection animation (scale up + fade out + particle burst),
   * triggers sound event, and marks as collected.
   *
   * Requirement 7.4: Play a collection sound effect and display a brief particle animation.
   * Requirement 7.5: Distinct visual feedback on collection.
   *
   * Emits 'collectible-collected' event on the scene with { id, type, value }.
   */
  collect(): void {
    if (this._isCollected) return;

    this._isCollected = true;

    // Emit collection event on the scene (for CollectibleSystem integration)
    this.scene.events.emit('collectible-collected', {
      id: this._id,
      type: this._type,
      value: this._value,
    });

    // Trigger collection sound
    this.scene.events.emit('play-sound', 'collectibleCollect');

    // Spawn particle burst
    this.spawnParticleBurst();

    // Play collection animation: scale up + fade out
    this.scene.tweens.add({
      targets: this,
      scaleX: COLLECT_SCALE,
      scaleY: COLLECT_SCALE,
      alpha: 0,
      duration: COLLECT_DURATION,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  // ─── Private Methods ────────────────────────────────────────────────────────

  /**
   * Gets the color for this collectible based on its type and value.
   */
  private getColor(): string {
    if (this._type === 'letter') {
      const idx = this._value.charCodeAt(0) - 'A'.charCodeAt(0);
      return LETTER_COLORS[idx % LETTER_COLORS.length];
    } else {
      const idx = parseInt(this._value) - 1;
      return NUMBER_COLORS[idx % NUMBER_COLORS.length];
    }
  }

  /**
   * Spawns a burst of colored particles radiating outward from the collectible position.
   */
  private spawnParticleBurst(): void {
    const colors = this._type === 'letter'
      ? [0xff6b6b, 0xffa500, 0xffd700, 0x48c774, 0x3273dc, 0xb86bff]
      : [0x00d1ff, 0x48c774, 0xffd700, 0xff6b6b, 0x3273dc, 0xff85c0];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
      const color = colors[i % colors.length];
      const x = this.x;
      const y = this.y;

      const particle = this.scene.add.circle(x, y, 3 + Math.random() * 2, color, 1);
      particle.setDepth(LAYER_DEPTHS.POWER_UPS + 1);

      const targetX = x + Math.cos(angle) * PARTICLE_SPREAD;
      const targetY = y + Math.sin(angle) * PARTICLE_SPREAD;

      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scale: 0.2,
        duration: PARTICLE_LIFETIME,
        ease: 'Quad.easeOut',
        onComplete: () => {
          particle.destroy();
        },
      });
    }
  }
}
